import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  ArrowForward,
  Launch,
  Visibility,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fadeInUp,
  cardHover,
} from '../styles/animations';

interface ModernCardProps {
  image: string;
  title: string;
  description: string;
  link?: string;
  linkText?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'service' | 'project' | 'testimonial';
  showOverlay?: boolean;
  onClick?: () => void;
}

const ModernCard: React.FC<ModernCardProps> = ({
  image,
  title,
  description,
  link,
  linkText,
  icon,
  variant = 'default',
  showOverlay = true,
  onClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (link) {
      navigate(link);
    }
  };

  const renderCardContent = () => {
    switch (variant) {
      case 'service':
        return (
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              {icon && (
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {icon}
                </Avatar>
              )}
              <Typography
                variant="h6"
                component="h3"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                {description}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowForward />}
                onClick={handleClick}
                sx={{
                  borderRadius: '50px',
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  },
                }}
              >
                {linkText || t("contenu.texte77")}
              </Button>
            </Box>
          </CardContent>
        );

      case 'project':
        return (
          <>
            <CardMedia
              component="img"
              height="200"
              image={image}
              alt={title}
              sx={{
                transition: 'transform 0.3s ease',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                component="h3"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                {description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  endIcon={<Launch />}
                  onClick={handleClick}
                  sx={{
                    textTransform: 'none',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  {linkText || t("voir-details")}
                </Button>
                <IconButton
                  size="small"
                  onClick={handleClick}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  <Visibility />
                </IconButton>
              </Box>
            </CardContent>
          </>
        );

      case 'testimonial':
        return (
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Avatar
              src={image}
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                border: '3px solid',
                borderColor: 'primary.main',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}
            >
              "{description}"
            </Typography>
            <Typography
              variant="h6"
              component="h3"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {linkText}
            </Typography>
          </CardContent>
        );

      default:
        return (
          <>
            {image && (
              <CardMedia
                component="img"
                height="200"
                image={image}
                alt={title}
                sx={{
                  transition: 'transform 0.3s ease',
                }}
              />
            )}
            <CardContent sx={{ p: 3 }}>
              {icon && (
                <Box sx={{ mb: 2 }}>
                  {icon}
                </Box>
              )}
              <Typography
                variant="h6"
                component="h3"
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                {description}
              </Typography>
              {link && (
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={handleClick}
                  sx={{
                    borderRadius: '50px',
                    textTransform: 'none',
                  }}
                >
                  {linkText || t("contenu.texte77")}
                </Button>
              )}
            </CardContent>
          </>
        );
    }
  };

  return (
    <motion.div
      variants={fadeInUp}
      initial="initial"
      animate="animate"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
    >
      <Card
        component={motion.div}
        {...cardHover}
        onClick={handleClick}
        sx={{
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            '& .card-media': {
              transform: 'scale(1.05)',
            },
          },
        }}
      >
        {showOverlay && variant === 'project' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              zIndex: 1,
              pointerEvents: 'none',
            }}
            className="card-overlay"
          />
        )}
        
        {renderCardContent()}
      </Card>
    </motion.div>
  );
};

export default ModernCard;
